import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

const MIN_STAR_COUNT = 90;
const MAX_STAR_COUNT = 220;
const MAX_SPEED = 3.5;

type Star = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  baseOpacity: number;
  phase: number;
  depth: number;
};

const UniverseBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;
    let running = true;
    const stars: Star[] = [];

    const getSize = () => ({
      w: window.innerWidth,
      h: window.innerHeight,
    });

    const spawnStar = (cx: number, cy: number): Star => {
      const angle = Math.random() * Math.PI * 2;
      const depth = Math.random();
      const speed = (0.2 + depth * MAX_SPEED) * (0.6 + Math.random() * 0.8);
      const size = 0.3 + depth * 1.8 + Math.random() * 0.5;
      const baseOpacity = 0.25 + depth * 0.6 + Math.random() * 0.2;
      return {
        x: cx + (Math.random() - 0.5) * 40,
        y: cy + (Math.random() - 0.5) * 40,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: Math.max(0.4, size),
        baseOpacity: Math.min(1, baseOpacity),
        phase: Math.random() * Math.PI * 2,
        depth,
      };
    };

    const getStarCount = (w: number, h: number) => {
      const areaBasedCount = Math.floor((w * h) / 9000);
      return Math.max(MIN_STAR_COUNT, Math.min(MAX_STAR_COUNT, areaBasedCount));
    };

    const setSize = () => {
      const { w, h } = getSize();
      if (w <= 0 || h <= 0) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const cx = w / 2;
      const cy = h / 2;
      const starCount = getStarCount(w, h);
      stars.length = 0;
      for (let i = 0; i < starCount; i++) {
        stars.push(spawnStar(cx, cy));
      }
    };

    const draw = () => {
      if (!running) return;
      const { w, h } = getSize();
      const cx = w / 2;
      const cy = h / 2;

      ctx.fillStyle = '#030712';
      ctx.fillRect(0, 0, w, h);

      stars.forEach((s) => {
        s.x += s.vx;
        s.y += s.vy;
        const distFromCenter = Math.hypot(s.x - cx, s.y - cy);
        const margin = 80;
        if (distFromCenter > Math.max(w, h) * 0.6 + margin || s.x < -margin || s.x > w + margin || s.y < -margin || s.y > h + margin) {
          const angle = Math.random() * Math.PI * 2;
          const speed = (0.2 + s.depth * MAX_SPEED) * (0.6 + Math.random() * 0.8);
          s.x = cx + (Math.random() - 0.5) * 60;
          s.y = cy + (Math.random() - 0.5) * 60;
          s.vx = Math.cos(angle) * speed;
          s.vy = Math.sin(angle) * speed;
        }
      });

      stars.forEach((s) => {
        const opacity = Math.max(0.15, s.baseOpacity);
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
        ctx.fill();
        if (s.depth > 0.6 && s.size > 1.2) {
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.size * 0.4, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 255, 255, ${opacity * 0.9})`;
          ctx.fill();
        }
      });

      animationId = requestAnimationFrame(draw);
    };

    setSize();
    window.addEventListener('resize', setSize);
    draw();

    return () => {
      running = false;
      window.removeEventListener('resize', setSize);
      if (animationId !== undefined) cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        pointerEvents: 'none',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
        }}
      />
    </Box>
  );
};

export default UniverseBackground;
